import React from 'react';
import { motion } from 'motion/react';
import { Loader2 } from 'lucide-react';

// Button variants
export type ButtonVariant = 
  | 'primary' 
  | 'secondary' 
  | 'danger' 
  | 'ghost' 
  | 'outline' 
  | 'link';

// Button sizes
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

// Button props interface
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

// Button variant styles
const buttonVariants = {
  primary: 'bg-lime-500 text-black hover:bg-lime-400 focus:ring-lime-500 border-lime-500',
  secondary: 'bg-muted text-muted-foreground hover:bg-bg-hover focus:ring-muted border-muted',
  danger: 'bg-destructive text-destructive-foreground hover:bg-destructive focus:ring-destructive border-destructive',
  ghost: 'bg-transparent text-muted-foreground hover:bg-bg-hover focus:ring-muted border-transparent',
  outline: 'bg-transparent text-muted-foreground border-border hover:bg-bg-hover focus:ring-muted',
  link: 'bg-transparent text-lime-400 hover:text-lime-300 focus:ring-lime-500 border-transparent underline'
};

// Button size styles
const buttonSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
  xl: 'px-8 py-4 text-lg'
};

// Disabled state styles
const disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-opacity-50';

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  const isDisabled = disabled || loading;
  
  const baseStyles = `
    inline-flex items-center justify-center font-medium rounded-lg
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${buttonVariants[variant]}
    ${buttonSizes[size]}
    ${fullWidth ? 'w-full' : ''}
    ${isDisabled ? disabledStyles : ''}
  `;

  return (
    // Cast to suppress incompatible onDrag typing from React vs Motion types
    <motion.button
      className={`${baseStyles} ${className}`}
      disabled={isDisabled}
      whileHover={!isDisabled ? { scale: 1.02 } : {}}
      whileTap={!isDisabled ? { scale: 0.98 } : {}}
      transition={{ duration: 0.1 }}
      {...(props as any)}
    >
      {/* Loading spinner */}
      {loading && (
        <Loader2 className="animate-spin mr-2 h-4 w-4" />
      )}
      
      {/* Left icon */}
      {!loading && leftIcon && (
        <span className="mr-2">{leftIcon}</span>
      )}
      
      {/* Button content */}
      <span>{children}</span>
      
      {/* Right icon */}
      {!loading && rightIcon && (
        <span className="ml-2">{rightIcon}</span>
      )}
    </motion.button>
  );
};

export default Button;
